import {useContext, useEffect, useMemo} from "react";
import { AuthContext } from "../context/AuthContext";
import axios from "axios";

export const useAxiosPrivate = () => {
    const { auth, updateToken, logout } = useContext(AuthContext);

    const axiosPrivate = useMemo(() => {
        return axios.create({
            baseURL: process.env.REACT_APP_BASE_URL,
            headers: {"Content-Type": "application/json"}
        })
    }, []);

    useEffect(() => {
        // --- REQUEST INTERCEPTOR ---
        const requestInterceptor = axiosPrivate.interceptors.request.use(
            (config) => {
                if (auth?.access && !config.headers["Authorization"]) {
                    config.headers["Authorization"] = `Bearer ${auth.access}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        // --- RESPONSE INTERCEPTOR ---
        const responseInterceptor = axiosPrivate.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // ------------------------------------
                // 1. Token refresh for 401
                // ------------------------------------
                if (error?.response?.status === 401 && !originalRequest._retry) {
                    originalRequest._retry = true;

                    try {
                        const tokens = await updateToken();

                        originalRequest.headers["Authorization"] = `Bearer ${tokens.access}`;
                        return axiosPrivate(originalRequest);
                    } catch (err) {
                        logout();
                        return Promise.reject(err);
                    }
                }

                // ------------------------------------
                // 2. Normalize DRF errors
                // ------------------------------------
                const backend = error?.response?.data;
                const normalized = [];
                if (backend?.errors) {
                    // Already normalized by DRF global exception handler
                    return Promise.reject({
                        errors: backend.errors
                    });

                }
                if (backend && typeof backend === "object") {
                    for (const [key, value] of Object.entries(backend)) {
                        if (key === "non_field_errors") {
                            if (Array.isArray(value)) {
                                value.forEach(msg => normalized.push({ field: null, message: msg }));
                            }
                        } else if (Array.isArray(value)) {
                            value.forEach(msg => normalized.push({ field: key, message: msg }));
                        } else if (typeof value === "string") {
                            normalized.push({ field: key, message: value });
                        }
                    }
                }

                if (normalized.length > 0) {
                    return Promise.reject(normalized);
                }

                // Hard fallback
                return Promise.reject([
                    { field: null, message: "Váratlan hiba történt! :(" }
                ]);
            }
        );

        return () => {
            axiosPrivate.interceptors.request.eject(requestInterceptor);
            axiosPrivate.interceptors.response.eject(responseInterceptor);
        };
    }, [auth.access, auth.refresh]);

    return axiosPrivate;
};
